import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import { api } from '../../lib/api'
import { downloadBlob } from '../../lib/download'
import { formatTenantDateTime, formatTenantTime } from '../../lib/dates'

type RentalStatus = 'active' | 'completed' | 'emergency'

interface RentalLine {
  id: string
  bike: { code: string; type: string; model?: string }
  tariff?: { name: string }
  basePrice?: string
}

interface PaymentLine {
  id: string
  type: string
  amount: number | string
  paymentMethod?: string | null
  note?: string | null
  isOwnerCorrection?: boolean
  rental?: { lines: { bike: { code: string } }[]; bike?: { code: string } | null } | null
}

interface Rental {
  id: string; status: RentalStatus;
  rentalNumber?: string | null
  pricingMode?: string
  source?: 'auto' | 'manual'
  manualNote?: string | null
  bike?: { code: string; type: string; model?: string } | null
  lines: RentalLine[]
  customer: { phone: string }
  customerName?: string
  manager: { name: string }
  pickupPoint: { name: string }
  returnPoint?: { name: string }
  actualStartAt: string; plannedReturnAt: string; actualReturnAt?: string
  basePrice: string; totalPrice?: string; priceOverride?: string; priceOverrideReason?: string
  prepaymentAmount?: string; paymentMethod?: string
  managerNotes?: string; requiresAttention?: boolean
  payments?: PaymentLine[]
}

interface Booking {
  id: string
  bike: { code: string; type: string; model?: string }
  manager: { name: string }
  customerPhone: string
  customerName?: string
  scheduledStartAt: string
  prepaymentAmount: string
  status: string
}

function bikesLabel(r: Rental): string {
  if (r.lines.length > 0) return r.lines.map((l) => `#${l.bike.code}`).join(', ')
  return r.bike ? `#${r.bike.code}` : '—'
}
function bikesDetail(r: Rental): string {
  if (r.lines.length > 0) {
    if (r.lines.length === 1) {
      const b = r.lines[0]?.bike
      return b ? `${b.type}${b.model ? ` · ${b.model}` : ''}` : ''
    }
    return `${r.lines.length} вел.`
  }
  return r.bike ? `${r.bike.type}${r.bike.model ? ` · ${r.bike.model}` : ''}` : ''
}

function formatDuration(ms: number) {
  const m = Math.floor(ms / 60000)
  const h = Math.floor(m / 60); const min = m % 60
  if (h > 0) return `${h} ч ${min > 0 ? min + ' мин' : ''}`
  return `${min} мин`
}

export default function RentalsPage() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const requiresAttentionFromUrl = searchParams.get('requiresAttention') === 'true'
  const statusFromUrl = searchParams.get('status') ?? 'active'

  const [tab, setTab] = useState<'rentals' | 'bookings'>('rentals')
  const [status, setStatus] = useState<string>(statusFromUrl)
  const [requiresAttention, setRequiresAttention] = useState(requiresAttentionFromUrl)
  const [detail, setDetail] = useState<Rental | null>(null)
  const [editPrice, setEditPrice] = useState('')
  const [editReason, setEditReason] = useState('')
  const [priceAuditError, setPriceAuditError] = useState<string | null>(null)
  const [cancelBookingId, setCancelBookingId] = useState<string | null>(null)
  const [cancelAmount, setCancelAmount] = useState('')
  const [cancelMethod, setCancelMethod] = useState<'cash' | 'card' | 'transfer'>('cash')
  const [showConvert, setShowConvert] = useState(false)
  const [convertDays, setConvertDays] = useState(1)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [completedPage, setCompletedPage] = useState(1)
  const [showAdminTools, setShowAdminTools] = useState(false)
  const [manualOnly, setManualOnly] = useState(false)

  useEffect(() => {
    setStatus(statusFromUrl)
    setRequiresAttention(requiresAttentionFromUrl)
  }, [statusFromUrl, requiresAttentionFromUrl])

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search.trim()), 350)
    return () => window.clearTimeout(t)
  }, [search])

  useEffect(() => {
    setCompletedPage(1)
  }, [debouncedSearch, status, requiresAttention, manualOnly])

  const completedLimit = 50
  const activeLimit = 150

  const queryParams = new URLSearchParams({
    status,
    limit: String(status === 'completed' ? completedLimit : activeLimit),
  })
  if (requiresAttention) queryParams.set('requiresAttention', 'true')
  if (debouncedSearch.length > 0) queryParams.set('search', debouncedSearch)
  if (manualOnly) queryParams.set('source', 'manual')
  if (status === 'completed') queryParams.set('page', String(completedPage))

  const { data } = useQuery({
    queryKey: ['rentals', status, requiresAttention, debouncedSearch, manualOnly, status === 'completed' ? completedPage : 0],
    queryFn: () => api.get(`/rentals?${queryParams}`).then((r) => r.data),
    staleTime: status === 'active' ? 30_000 : 2 * 60_000,
    refetchInterval: status === 'active' ? 60_000 : false,
  })

  const { data: detailData } = useQuery({
    queryKey: ['rental-detail', detail?.id],
    queryFn: () => api.get(`/rentals/${detail!.id}`).then((r) => r.data.rental as Rental),
    enabled: !!detail?.id,
  })

  const updateRental = useMutation({
    mutationFn: ({ id, ...body }: { id: string; priceOverride?: number; priceOverrideReason?: string }) =>
      api.patch(`/rentals/${id}`, body),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['rentals'] })
      qc.invalidateQueries({ queryKey: ['rental-detail', res.data.rental?.id] })
      setDetail(res.data.rental)
      setPriceAuditError(null)
    },
  })

  const { data: bookingsData } = useQuery({
    queryKey: ['bookings', 'owner', 'pending'],
    queryFn: () => api.get('/bookings?status=pending').then((r) => r.data),
    enabled: tab === 'bookings',
    staleTime: 45_000,
    refetchInterval: 60_000,
  })

  const cancelBookingMutation = useMutation({
    mutationFn: ({ id, amount, paymentMethod }: { id: string; amount?: number; paymentMethod: 'cash' | 'card' | 'transfer' }) =>
      api.delete(`/bookings/${id}`, { data: { amount: amount !== undefined && !isNaN(amount) ? amount : undefined, paymentMethod } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bookings'] })
      setCancelBookingId(null)
      setCancelAmount('')
    },
  })

  const convertToDaily = useMutation({
    mutationFn: ({ id, days }: { id: string; days: number }) =>
      api.post(`/rentals/${id}/convert-to-daily`, { days }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['rentals'] })
      qc.invalidateQueries({ queryKey: ['rental-detail', res.data.rental?.id] })
      setDetail(res.data.rental)
      setShowConvert(false)
    },
  })

  const convertToHourly = useMutation({
    mutationFn: ({ id }: { id: string }) =>
      api.post(`/rentals/${id}/convert-to-hourly`),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['rentals'] })
      qc.invalidateQueries({ queryKey: ['rental-detail', res.data.rental?.id] })
      setDetail(res.data.rental)
    },
  })

  const handleExport = async () => {
    try {
      const res = await api.get('/exports/rentals.csv', { responseType: 'blob' })
      await downloadBlob(res.data as Blob, 'rentals.csv')
    } catch {
      alert('Ошибка экспорта. Убедитесь что вы авторизованы как владелец.')
    }
  }

  const openDetail = (r: Rental) => {
    setDetail(r)
    setEditPrice(r.priceOverride ?? r.totalPrice ?? r.basePrice)
    setEditReason(r.priceOverrideReason ?? '')
    setPriceAuditError(null)
    setShowConvert(false)
    setConvertDays(1)
    setShowAdminTools(false)
  }

  // Open a specific rental when navigated here from «Требует внимания» (#1)
  useEffect(() => {
    const st = location.state as { openRentalId?: string } | null
    if (!st?.openRentalId) return
    const id = st.openRentalId
    window.history.replaceState({}, '')
    api.get(`/rentals/${id}`).then((r) => { if (r.data?.rental) openDetail(r.data.rental) }).catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state])

  const customerLabel = (r: Rental) => r.customerName?.trim() || r.customer.phone

  // Open a specific rental when navigated from «Требует внимания» (#1)
  useEffect(() => {
    const openRentalId = (location.state as { openRentalId?: string } | null)?.openRentalId
    if (!openRentalId) return
    let cancelled = false
    api.get(`/rentals/${openRentalId}`)
      .then((res) => { if (!cancelled && res.data?.rental) openDetail(res.data.rental) })
      .catch(() => {})
    window.history.replaceState({}, '')
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state])

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold">Аренды</h2>
        {tab === 'rentals' && (
          <div className="flex gap-2">
            <button
              onClick={() => navigate('/owner/rentals/manual')}
              className="border border-amber-300 text-amber-700 px-3 py-2 rounded-xl text-sm font-medium hover:bg-amber-50"
            >
              📝 Ручная
            </button>
            <button onClick={handleExport} className="border border-gray-300 text-gray-700 px-4 py-2 rounded-xl text-sm font-medium hover:bg-gray-50">
              CSV ↓
            </button>
          </div>
        )}
      </div>

      {/* Tab switcher */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setTab('rentals')}
          className={`px-4 py-2 rounded-xl text-sm font-medium ${tab === 'rentals' ? 'bg-brand-600 text-white' : 'border border-gray-300 text-gray-600'}`}
        >
          Аренды
        </button>
        <button
          onClick={() => setTab('bookings')}
          className={`px-4 py-2 rounded-xl text-sm font-medium ${tab === 'bookings' ? 'bg-brand-600 text-white' : 'border border-gray-300 text-gray-600'}`}
        >
          Бронирования
        </button>
      </div>

      {/* Bookings tab */}
      {tab === 'bookings' && (
        <div className="space-y-3">
          {!bookingsData?.bookings?.length && <p className="text-gray-400 text-sm py-4 text-center">Нет активных бронирований</p>}
          {bookingsData?.bookings?.map((b: Booking) => (
            <div key={b.id} className="bg-white border border-gray-200 rounded-2xl p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-semibold">#{b.bike.code} · {b.bike.type}{b.bike.model ? ` · ${b.bike.model}` : ''}</p>
                  <p className="text-sm text-gray-500">{b.customerPhone}{b.customerName ? ` · ${b.customerName}` : ''}</p>
                  <p className="text-xs text-gray-400">{b.manager.name}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-amber-600">{b.prepaymentAmount} ₽</p>
                  <p className="text-xs text-gray-400">{formatTenantDateTime(b.scheduledStartAt)}</p>
                </div>
              </div>
              <button
                onClick={() => { setCancelBookingId(b.id); setCancelAmount('') }}
                className="mt-3 w-full border border-red-200 text-red-600 rounded-xl py-2 text-sm font-medium hover:bg-red-50"
              >
                Отменить бронирование
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Cancel booking modal */}
      {cancelBookingId && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setCancelBookingId(null)}>
          <div className="bg-white rounded-2xl p-5 w-full max-w-sm space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold text-gray-900">Отмена бронирования</h3>
            <p className="text-sm text-gray-500">Укажите фактическую сумму. Положительная — деньги получены от клиента, отрицательная — возврат клиенту.</p>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Сумма (₽), можно с минусом</label>
              <input
                type="number"
                value={cancelAmount}
                onChange={(e) => setCancelAmount(e.target.value)}
                placeholder="0"
                step={50}
                className="w-full border border-gray-300 rounded-xl px-4 py-3 text-base text-center font-bold"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Способ оплаты</label>
              <div className="grid grid-cols-3 gap-2">
                {(['cash', 'card', 'transfer'] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setCancelMethod(m)}
                    className={`py-2 rounded-xl border text-xs font-medium ${cancelMethod === m ? 'bg-brand-600 text-white border-brand-600' : 'border-gray-300 text-gray-600'}`}
                  >
                    {m === 'cash' ? '💵 Нал' : m === 'card' ? '💳 Безнал' : '📲 Перевод'}
                  </button>
                ))}
              </div>
            </div>
            {cancelBookingMutation.isError && (
              <p className="text-red-500 text-xs">
                {(cancelBookingMutation.error as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? 'Ошибка'}
              </p>
            )}
            <div className="flex gap-2">
              <button
                onClick={() => cancelBookingMutation.mutate({
                  id: cancelBookingId,
                  amount: cancelAmount !== '' ? parseFloat(cancelAmount) : undefined,
                  paymentMethod: cancelMethod,
                })}
                disabled={cancelBookingMutation.isPending}
                className="flex-1 bg-red-600 text-white py-3 rounded-xl text-sm font-semibold disabled:opacity-60"
              >
                {cancelBookingMutation.isPending ? 'Отмена...' : 'Подтвердить отмену'}
              </button>
              <button onClick={() => setCancelBookingId(null)} className="flex-1 border border-gray-300 py-3 rounded-xl text-sm text-gray-600">
                Назад
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rentals tab */}
      {tab === 'rentals' && <>
      <div className="flex flex-wrap gap-2 mb-3 items-center">
        {(['active', 'completed'] as const).map((s) => (
          <button
            key={s}
            onClick={() => {
              setStatus(s)
              if (s === 'completed') setRequiresAttention(false)
              setSearch('')
              setCompletedPage(1)
            }}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${status === s ? 'bg-brand-600 text-white' : 'border border-gray-300 text-gray-600'}`}
          >
            {s === 'active' ? 'Активные' : 'Завершённые'}
          </button>
        ))}
        {requiresAttention && status === 'active' && (
          <span className="px-3 py-1.5 rounded-xl text-xs font-medium bg-orange-100 text-orange-700 flex items-center gap-1">
            ⚠️ Требуют внимания
            <button onClick={() => setRequiresAttention(false)} className="text-orange-500 hover:text-orange-700">×</button>
          </span>
        )}
      </div>

      {/* Поиск по клиенту / велосипеду */}
      <div className="relative mb-2">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Поиск (актуально по всем арендам): имя, телефон, велик, точка, менеджер…"
          className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm pl-9 bg-gray-50 focus:outline-none focus:border-brand-400"
        />
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🔍</span>
        {search && (
          <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-lg leading-none">×</button>
        )}
      </div>

      <label className="flex items-center gap-2 text-xs text-gray-600 mb-4">
        <input type="checkbox" checked={manualOnly} onChange={(e) => setManualOnly(e.target.checked)} />
        📝 Только ручные аренды
      </label>

      {(() => {
        const list: Rental[] = data?.rentals ?? []
        const total: number | undefined = typeof data?.total === 'number' ? data.total : undefined
        const limit = typeof data?.limit === 'number' ? data.limit : completedLimit
        const pageNum = typeof data?.page === 'number' ? data.page : completedPage
        const hasPager = status === 'completed' && total !== undefined && total > limit
        const from = list.length === 0 ? 0 : (pageNum - 1) * limit + 1
        const to = list.length === 0 ? 0 : (pageNum - 1) * limit + list.length

        return (
      <div className="space-y-3">
        {status === 'completed' && total !== undefined && (
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-gray-600">
            <span>
              {total === 0 ? 'Нет записей' : `Показано ${from}–${to} из ${total}`}
            </span>
            {hasPager ? (
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={completedPage <= 1}
                  onClick={() => setCompletedPage((p) => Math.max(1, p - 1))}
                  className="px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed text-sm font-medium"
                >
                  Назад
                </button>
                <button
                  type="button"
                  disabled={pageNum * limit >= total}
                  onClick={() => setCompletedPage((p) => p + 1)}
                  className="px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed text-sm font-medium"
                >
                  Вперёд
                </button>
              </div>
            ) : null}
          </div>
        )}
        {!list.length && <p className="text-gray-400 text-sm py-4 text-center">{debouncedSearch ? 'Ничего не найдено' : 'Аренд нет'}</p>}
        {list.map((r: Rental) => (
          <div key={r.id} className={`bg-white border rounded-2xl p-4 cursor-pointer transition-colors hover:border-brand-300 ${r.requiresAttention ? 'border-orange-300' : 'border-gray-200'}`} onClick={() => openDetail(r)}>
            <div className="flex items-start justify-between">
              <div>
                <p className="font-semibold flex items-center gap-2 flex-wrap">
                  <span>{bikesLabel(r)} · {bikesDetail(r)}</span>
                  {r.rentalNumber && <span className="text-[10px] font-mono text-gray-400">#{r.rentalNumber}</span>}
                  {r.source === 'manual' && (
                    <span
                      title={r.manualNote ?? 'Создана владельцем вручную'}
                      className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-full font-medium"
                    >📝 ручная</span>
                  )}
                </p>
                <p className="text-sm text-gray-700">{customerLabel(r)}</p>
                <p className="text-xs text-gray-400">{r.manager.name} · {r.pickupPoint.name}</p>
                {r.requiresAttention && <p className="text-xs text-orange-600 font-medium mt-0.5">⚠️ Требует внимания</p>}
                {r.managerNotes && <p className="text-xs text-gray-400 mt-0.5 truncate max-w-[220px]">💬 {r.managerNotes}</p>}
                {r.returnPoint && r.returnPoint.name !== r.pickupPoint.name && (
                  <p className="text-xs text-orange-500">→ Сдан в: {r.returnPoint.name}</p>
                )}
              </div>
              <div className="text-right">
                <p className="font-bold text-green-600">{r.totalPrice ?? r.basePrice} ₽</p>
                <p className="text-xs text-gray-400">
                  {r.status === 'completed' && r.actualReturnAt
                    ? `Закрыта: ${formatTenantDateTime(r.actualReturnAt)}`
                    : formatTenantDateTime(r.actualStartAt)}
                </p>
                {r.status === 'active' && (
                  <p className="text-xs text-blue-500">до {formatTenantTime(r.plannedReturnAt)}</p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
        )
      })()}

      {/* Rental detail / edit modal */}
      {detail && (
        <div className="fixed inset-0 bg-black/40 z-40 flex items-end" onClick={() => setDetail(null)}>
          <div className="bg-white w-full rounded-t-3xl p-5 space-y-4 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-bold text-base">{bikesLabel(detail)} · {bikesDetail(detail)}</h3>
                <p className="text-sm text-gray-700">{customerLabel(detail)}</p>
                {detail.customerName && <p className="text-xs text-gray-400">{detail.customer.phone}</p>}
                <p className="text-xs text-gray-400">{detail.manager.name} · {detail.pickupPoint.name}</p>
              </div>
              <button onClick={() => setDetail(null)} className="text-gray-400 text-2xl leading-none">×</button>
            </div>

            <div className="bg-gray-50 rounded-xl p-3 space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Начало</span><span>{formatTenantDateTime(detail.actualStartAt)}</span></div>
              {detail.actualReturnAt && (
                <>
                  <div className="flex justify-between"><span className="text-gray-500">Конец</span><span>{formatTenantDateTime(detail.actualReturnAt)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Длительность</span><span>{formatDuration(new Date(detail.actualReturnAt).getTime() - new Date(detail.actualStartAt).getTime())}</span></div>
                </>
              )}
              <div className="flex justify-between font-semibold pt-1 border-t">
                <span>Итого</span><span className="text-green-600">{detail.totalPrice ?? detail.basePrice} ₽</span>
              </div>
            </div>

            {detail.requiresAttention && (
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-3">
                <p className="text-orange-700 text-sm font-semibold">⚠️ Требует внимания</p>
              </div>
            )}

            {detail.managerNotes && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                <p className="text-xs text-blue-500 mb-0.5">Комментарий менеджера</p>
                <p className="text-sm text-blue-800">{detail.managerNotes}</p>
              </div>
            )}

            {/* Конвертация режима тарификации (только активные) */}
            {detail.status === 'active' && (() => {
              const mode = (detailData ?? detail).pricingMode
              return (
                <>
                  {mode === 'hourly' && (
                    <div className="border border-blue-200 rounded-xl overflow-hidden">
                      <button
                        onClick={() => setShowConvert((v) => !v)}
                        className="w-full flex justify-between items-center px-4 py-3 bg-blue-50 text-blue-700 text-sm font-medium"
                      >
                        <span>Перевести в суточную</span>
                        <span>{showConvert ? '\u25B2' : '\u25BC'}</span>
                      </button>
                      {showConvert && (
                        <div className="p-4 space-y-3 bg-white">
                          <p className="text-xs text-gray-500">Тариф пересчитается с начала аренды по суточному тарифу.</p>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Количество суток</label>
                            <div className="flex items-center gap-3">
                              <button
                                onClick={() => setConvertDays((d) => Math.max(1, d - 1))}
                                className="w-10 h-10 rounded-xl border border-gray-300 text-lg font-bold flex items-center justify-center"
                              >&minus;</button>
                              <span className="text-xl font-bold w-10 text-center">{convertDays}</span>
                              <button
                                onClick={() => setConvertDays((d) => Math.min(30, d + 1))}
                                className="w-10 h-10 rounded-xl border border-gray-300 text-lg font-bold flex items-center justify-center"
                              >+</button>
                            </div>
                          </div>
                          {convertToDaily.isError && (
                            <p className="text-red-500 text-xs">
                              {(convertToDaily.error as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? 'Ошибка'}
                            </p>
                          )}
                          <div className="flex gap-2">
                            <button
                              onClick={() => convertToDaily.mutate({ id: detail.id, days: convertDays })}
                              disabled={convertToDaily.isPending}
                              className="flex-1 bg-blue-600 text-white py-2.5 rounded-xl text-sm font-semibold disabled:opacity-60"
                            >
                              {convertToDaily.isPending ? 'Конвертация...' : `Перевести (${convertDays} сут.)`}
                            </button>
                            <button onClick={() => setShowConvert(false)} className="px-4 border border-gray-300 rounded-xl text-sm text-gray-500">
                              Отмена
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {mode === 'daily' && (
                    <div className="border border-orange-200 rounded-xl overflow-hidden">
                      <button
                        onClick={() => {
                          if (window.confirm('Перевести аренду в часовой тариф? Стоимость будет пересчитана по фактически прошедшим часам.')) {
                            convertToHourly.mutate({ id: detail.id })
                          }
                        }}
                        disabled={convertToHourly.isPending}
                        className="w-full flex justify-between items-center px-4 py-3 bg-orange-50 text-orange-700 text-sm font-medium disabled:opacity-60"
                      >
                        <span>Перевести в часовой тариф</span>
                        {convertToHourly.isPending && <span className="text-xs">...</span>}
                      </button>
                      {convertToHourly.isError && (
                        <p className="text-red-500 text-xs p-2">
                          {(convertToHourly.error as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? 'Ошибка'}
                        </p>
                      )}
                    </div>
                  )}
                </>
              )
            })()}

            {/* Таблица платежей по аренде */}
            {(() => {
              const payments = detailData?.payments ?? []
              if (!payments.length) return null
              const TYPE_LABEL: Record<string, string> = { prepayment: 'Предоплата', topup: 'Оплата', refund: 'Возврат', cashout: 'Инкассация' }
              return (
                <div className="border border-gray-200 rounded-2xl p-4">
                  <p className="text-sm font-semibold text-gray-700 mb-3">Движение средств</p>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-gray-400 border-b border-gray-100">
                        <th className="text-left pb-1">Вид операции</th>
                        <th className="text-left pb-1">Инвентарь</th>
                        <th className="text-right pb-1">Сумма</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {payments.map((p) => {
                        const lines = p.rental?.lines ?? []
                        const codes = lines.length > 0 ? lines.map((l) => `#${l.bike.code}`).join(', ') : p.rental?.bike?.code ? `#${p.rental.bike.code}` : '—'
                        const amt = Number(p.amount)
                        const signed = p.type === 'refund' ? -amt : amt
                        return (
                          <tr key={p.id} className={p.isOwnerCorrection ? 'bg-amber-50' : ''}>
                            <td className="py-1.5 text-gray-700">
                              {TYPE_LABEL[p.type] ?? p.type}
                              {p.isOwnerCorrection && (
                                <span className="ml-1.5 inline-block text-[10px] font-semibold bg-amber-100 text-amber-700 border border-amber-300 rounded px-1 py-0.5 leading-none align-middle">
                                  корр. владельца
                                </span>
                              )}
                              {p.note && <span className="ml-1 text-xs text-gray-400">— {p.note}</span>}
                            </td>
                            <td className="py-1.5 text-gray-500">{codes}</td>
                            <td className={`py-1.5 text-right font-semibold ${signed < 0 ? 'text-red-500' : 'text-gray-800'}`}>
                              {signed < 0 ? `−${Math.abs(signed).toLocaleString('ru-RU')} ₽` : `${signed.toLocaleString('ru-RU')} ₽`}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )
            })()}

            {/* Owner tools: price edit + correction payment — collapsed by default */}
            <div className="border-t pt-3">
              <button
                onClick={() => setShowAdminTools((v) => !v)}
                className="w-full flex justify-between items-center py-2 text-sm font-medium text-gray-600 hover:text-gray-900"
              >
                <span>🔧 Инструменты владельца</span>
                <span className="text-gray-400">{showAdminTools ? '▲' : '▼'}</span>
              </button>
            </div>
            {showAdminTools && (
            <div className="space-y-4">
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">Изменить стоимость аренды</p>
                <p className="text-xs text-gray-500 mb-2">Без отдельной причины не сохраняется — нужен аудит (мин. 5 символов).</p>
                <input
                  type="number"
                  value={editPrice}
                  onChange={(e) => {
                    setEditPrice(e.target.value)
                    setPriceAuditError(null)
                  }}
                  placeholder="Новая цена"
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 text-base mb-2"
                />
                <input
                  value={editReason}
                  onChange={(e) => {
                    setEditReason(e.target.value)
                    setPriceAuditError(null)
                  }}
                  placeholder="Причина изменения цены (мин. 5 символов)"
                  className={`w-full border rounded-xl px-4 py-3 text-sm mb-2 ${priceAuditError ? 'border-red-400' : 'border-gray-300'}`}
                />
                {priceAuditError && <p className="text-red-600 text-xs mb-2">{priceAuditError}</p>}
                <button
                  type="button"
                  onClick={() => {
                    const reason = editReason.trim()
                    if (reason.length < 5) {
                      setPriceAuditError('Укажите причину не короче 5 символов — обязательно для аудита.')
                      return
                    }
                    const n = parseFloat(editPrice)
                    if (isNaN(n) || n < 0) {
                      setPriceAuditError('Укажите корректную сумму.')
                      return
                    }
                    setPriceAuditError(null)
                    updateRental.mutate({ id: detail.id, priceOverride: n, priceOverrideReason: reason })
                  }}
                  disabled={updateRental.isPending}
                  className="w-full bg-brand-600 text-white py-3 rounded-xl text-sm font-semibold disabled:opacity-60"
                >
                  {updateRental.isPending ? 'Сохранение...' : 'Сохранить стоимость'}
                </button>
              </div>

              <div className="bg-blue-50 rounded-2xl p-4 text-xs text-blue-800 space-y-1">
                <p className="font-semibold">Создать платёж по этой аренде</p>
                <p>Используйте раздел <strong>Платежи → «+ Создать платёж»</strong>, выберите аренду по коду велосипеда или имени клиента.</p>
              </div>
            </div>
            )}
          </div>
        </div>
      )}
      </>}
    </div>
  )
}
